import type { EnemyConfig } from "@bastion/data";
import type { EnemyState, Point } from "../types";
import type { ResolvedMap } from "../repos/ConfigRepository";
import { pixelToGrid, gridCellCentre, occupiedGridCells } from "../grid";

export interface BaseDistanceField {
  /** 返回该位置所在格到基地的最短可行路径长度；不可达时为 Infinity。 */
  distanceAt(position: Point): number;
}

/**
 * 基于当前炮台占用，从基地反向遍历四方向网格，计算每个格到基地的最短距离。
 */
export function createBaseDistanceField(
  towerPositions: { x: number; y: number }[],
  resolvedMap: ResolvedMap,
): BaseDistanceField {
  const { gridSize, width, height, base } = resolvedMap.map;
  const cols = Math.floor(width / gridSize);
  const rows = Math.floor(height / gridSize);
  const distances = new Int32Array(cols * rows);
  distances.fill(-1);
  const blocked = occupiedGridCells(towerPositions, gridSize);
  const baseCell = pixelToGrid(base.x, base.y, gridSize);
  const inBounds = (gx: number, gy: number): boolean =>
    gx >= 0 && gy >= 0 && gx < cols && gy < rows;

  if (inBounds(baseCell.gx, baseCell.gy) && !blocked.has(`${baseCell.gx},${baseCell.gy}`)) {
    const queue: number[] = [baseCell.gy * cols + baseCell.gx];
    distances[queue[0]] = 0;
    for (let head = 0; head < queue.length; head++) {
      const index = queue[head];
      const gx = index % cols;
      const gy = Math.floor(index / cols);
      const nextDistance = distances[index] + 1;
      const neighbors = [
        [gx - 1, gy],
        [gx + 1, gy],
        [gx, gy - 1],
        [gx, gy + 1],
      ];
      for (const [nextX, nextY] of neighbors) {
        if (!inBounds(nextX, nextY)) continue;
        const nextIndex = nextY * cols + nextX;
        if (distances[nextIndex] >= 0 || blocked.has(`${nextX},${nextY}`)) continue;
        distances[nextIndex] = nextDistance;
        queue.push(nextIndex);
      }
    }
  }

  return {
    distanceAt(position: Point): number {
      const cell = pixelToGrid(position.x, position.y, gridSize);
      if (!inBounds(cell.gx, cell.gy)) return Infinity;
      const distance = distances[cell.gy * cols + cell.gx];
      return distance < 0 ? Infinity : distance * gridSize;
    },
  };
}

/** 缓存同一地图几何与炮台占用下的基地距离场。 */
export class BaseDistanceFieldCache {
  private signature: string | null = null;
  private field: BaseDistanceField | null = null;

  get(towerPositions: { x: number; y: number }[], resolvedMap: ResolvedMap): BaseDistanceField {
    const { width, height, gridSize, base } = resolvedMap.map;
    const occupied = [...occupiedGridCells(towerPositions, gridSize)].sort();
    const signature = JSON.stringify([width, height, gridSize, base.x, base.y, occupied]);
    if (signature !== this.signature || !this.field) {
      this.signature = signature;
      this.field = createBaseDistanceField(towerPositions, resolvedMap);
    }
    return this.field;
  }
}

/**
 * 在完整地图网格上计算从出生点到基地的最短轴对齐路径。
 *  intentionally 不使用地图路径点作为路线约束。
 */
export function computeGlobalShortestPath(
  start: Point,
  goal: Point,
  towerPositions: { x: number; y: number }[],
  resolvedMap: ResolvedMap,
): Point[] | null {
  const { gridSize, width, height } = resolvedMap.map;
  const cols = Math.floor(width / gridSize);
  const rows = Math.floor(height / gridSize);
  const startCell = pixelToGrid(start.x, start.y, gridSize);
  const goalCell = pixelToGrid(goal.x, goal.y, gridSize);
  const inBounds = (cell: { gx: number; gy: number }): boolean =>
    cell.gx >= 0 && cell.gy >= 0 && cell.gx < cols && cell.gy < rows;
  if (!inBounds(startCell) || !inBounds(goalCell)) return null;

  const blocked = occupiedGridCells(towerPositions, gridSize);
  return computeGridAStar(
    gridCellCentre(startCell.gx, startCell.gy, gridSize),
    gridCellCentre(goalCell.gx, goalCell.gy, gridSize),
    blocked,
    cols,
    rows,
    gridSize,
  );
}

export function replanEnemyPath(
  enemy: EnemyState,
  towerPositions: { x: number; y: number }[],
  resolvedMap: ResolvedMap,
): Point[] | null {
  const gridSize = resolvedMap.map.gridSize;
  const currentCell = pixelToGrid(enemy.position.x, enemy.position.y, gridSize);
  const currentKey = `${currentCell.gx},${currentCell.gy}`;
  const blocked = occupiedGridCells(towerPositions, gridSize);
  const currentTower = blocked.has(currentKey);
  const progress = currentSegmentProgress(enemy);

  if (currentTower && progress <= 0.5) {
    return computePathFromExactPosition(enemy.position, towerPositions, resolvedMap);
  }

  if (!currentTower) {
    return computePathFromExactPosition(enemy.position, towerPositions, resolvedMap);
  }

  const exit = nextPointOutsideCell(enemy, currentCell, gridSize);
  if (!exit) return null;
  const transition = [enemy.position, exit];
  const remainder = computeGlobalShortestPath(
    exit,
    resolvedMap.map.base,
    towerPositions,
    resolvedMap,
  );
  if (!remainder) return null;
  return [...transition, ...remainder.slice(1)];
}

function computePathFromExactPosition(
  position: Point,
  towerPositions: { x: number; y: number }[],
  resolvedMap: ResolvedMap,
): Point[] | null {
  const path = computeGlobalShortestPath(
    position,
    resolvedMap.map.base,
    towerPositions,
    resolvedMap,
  );
  if (!path || path.length < 2) return null;
  return [position, ...path.slice(1)];
}

function currentSegmentProgress(enemy: EnemyState): number {
  return enemy.segmentProgress;
}

function nextPointOutsideCell(
  enemy: EnemyState,
  cell: { gx: number; gy: number },
  gridSize: number,
): Point | null {
  const from = enemy.waypoints[enemy.currentWaypointIndex];
  const to = enemy.waypoints[enemy.currentWaypointIndex + 1];
  if (!from || !to) return null;
  const direction = { x: Math.sign(to.x - from.x), y: Math.sign(to.y - from.y) };
  if (direction.x === 0 && direction.y === 0) return null;
  return gridCellCentre(cell.gx + direction.x, cell.gy + direction.y, gridSize);
}

/**
 * 返回从 `src` 到 `dst`（含两端）的格子中心点数组，
 * 或当不存在路径时返回 null。移动为四方向（无对角线）。
 */
function computeGridAStar(
  src: Point,
  dst: Point,
  blocked: Set<string>,
  cols: number,
  rows: number,
  gridSize: number,
): Point[] | null {
  const s = pixelToGrid(src.x, src.y, gridSize);
  const g = pixelToGrid(dst.x, dst.y, gridSize);
  const key = (gx: number, gy: number) => `${gx},${gy}`;

  if (blocked.has(key(s.gx, s.gy)) || blocked.has(key(g.gx, g.gy))) {
    return null;
  }

  function h(ax: number, ay: number, bx: number, by: number): number {
    return Math.abs(ax - bx) + Math.abs(ay - by);
  }

  const gScore = new Map<string, number>();
  const fScore = new Map<string, number>();
  const cameFrom = new Map<string, string>();
  const startKey = key(s.gx, s.gy);
  gScore.set(startKey, 0);
  fScore.set(startKey, h(s.gx, s.gy, g.gx, g.gy));
  const openSet: string[] = [startKey];

  const dirs = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
  ];

  while (openSet.length > 0) {
    let curKey = openSet[0];
    let bestF = fScore.get(curKey) ?? Infinity;
    for (let i = 1; i < openSet.length; i++) {
      const f = fScore.get(openSet[i]) ?? Infinity;
      if (f < bestF) {
        bestF = f;
        curKey = openSet[i];
      }
    }

    const [cx, cy] = curKey.split(",").map(Number);

    if (cx === g.gx && cy === g.gy) {
      const path: Point[] = [];
      let cur: string | undefined = curKey;
      while (cur !== undefined) {
        const [gx, gy] = cur.split(",").map(Number);
        path.unshift(gridCellCentre(gx, gy, gridSize));
        cur = cameFrom.get(cur);
      }
      // 只保留转向点以提升性能（将路径复杂度从 O(cells) 降至 O(turns)）
      const turns: Point[] = [path[0]];
      for (let i = 1; i < path.length - 1; i++) {
        const prev = turns[turns.length - 1];
        const curr = path[i];
        const next = path[i + 1];
        const dir1 = { x: curr.x - prev.x, y: curr.y - prev.y };
        const dir2 = { x: next.x - curr.x, y: next.y - curr.y };
        // 如果方向改变则保留点（叉积 != 0）
        if (dir1.x * dir2.y !== dir1.y * dir2.x) {
          turns.push(curr);
        }
      }
      turns.push(path[path.length - 1]);
      return turns;
    }

    openSet.splice(openSet.indexOf(curKey), 1);

    for (const [dx, dy] of dirs) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const nkey = key(nx, ny);
      if (blocked.has(nkey)) continue;
      const tentG = (gScore.get(curKey) ?? 0) + 1;
      if (tentG < (gScore.get(nkey) ?? Infinity)) {
        cameFrom.set(nkey, curKey);
        gScore.set(nkey, tentG);
        fScore.set(nkey, tentG + h(nx, ny, g.gx, g.gy));
        if (!openSet.includes(nkey)) {
          openSet.push(nkey);
        }
      }
    }
  }

  return null;
}

/**
 * 沿预计算的路径点移动敌人一个 tick。
 * 使用 `enemy.currentWaypointIndex`（指向 enemy.waypoints 的索引）和
 * `enemy.segmentProgress`（沿当前段的进度 0..1）。
 * 写入 `enemy.position`、`enemy.currentWaypointIndex`、`enemy.segmentProgress`。
 *
 * 移动修正（眩晕、减速）从 `enemy.statusEffects` 派生。
 * 可选的 `enemyConfig` 在无速度相关状态效果激活时提供基础速度。
 */
export interface MoveResult {
  reachedBase: boolean;
  distanceTraveled: number;
}

export function moveEnemy(
  enemy: EnemyState,
  dt: number,
  _resolvedMap: ResolvedMap,
  enemyConfig?: EnemyConfig,
): MoveResult {
  if (enemy.debugStationary) {
    return { reachedBase: false, distanceTraveled: 0 };
  }

  const wp = enemy.waypoints;
  if (!wp || wp.length < 2) {
    return { reachedBase: false, distanceTraveled: 0 };
  }

  // 从 statusEffects 派生眩晕状态。
  const isStunned = enemy.statusEffects.some(
    (e) =>
      (e.controlType === "stun" || e.controlType === "root" || e.key === "stun") && e.remaining > 0,
  );

  // 从 statusEffects 派生减速因子：按规则相乘。
  const slowEffects = enemy.statusEffects.filter(
    (e) => e.controlType === "slow" && e.remaining > 0,
  );
  const slowFactor =
    slowEffects.length > 0 ? slowEffects.reduce((acc, e) => acc * (e.factor ?? 1), 1) : 1;

  // 基础速度来自配置；如果减速效果显式携带速度修饰符
  // via its StatModifier list, honour it (fall back to slowFactor derived above).
  const baseSpeed = enemy.speed ?? enemyConfig?.stats.speed ?? 0;
  const effectiveSpeed = baseSpeed * slowFactor;

  let travel = effectiveSpeed * dt;
  if (isStunned) {
    travel = 0;
  }

  let totalTraveled = 0;

  while (travel > 1e-9) {
    const fromIdx = enemy.currentWaypointIndex;
    if (fromIdx >= wp.length - 1) {
      enemy.position = { ...wp[wp.length - 1] };
      return { reachedBase: true, distanceTraveled: totalTraveled };
    }

    const from = wp[fromIdx];
    const to = wp[fromIdx + 1];
    const segLength = Math.hypot(to.x - from.x, to.y - from.y);

    if (segLength === 0) {
      enemy.currentWaypointIndex += 1;
      continue;
    }

    const progressWithin = enemy.segmentProgress;
    const distanceRemaining = (1 - progressWithin) * segLength;
    const step = Math.min(travel, distanceRemaining);
    const t = progressWithin + step / segLength;

    enemy.position = {
      x: from.x + (to.x - from.x) * t,
      y: from.y + (to.y - from.y) * t,
    };
    travel -= step;
    totalTraveled += step;

    if (step >= distanceRemaining - 1e-9) {
      enemy.currentWaypointIndex += 1;
      enemy.segmentProgress = 0;
    } else {
      enemy.segmentProgress = t;
      break;
    }
  }

  if (enemy.currentWaypointIndex >= wp.length - 1) {
    enemy.position = { ...wp[wp.length - 1] };
    return { reachedBase: true, distanceTraveled: totalTraveled };
  }

  return { reachedBase: false, distanceTraveled: totalTraveled };
}
